import { synthesiseUrl } from "./helpers";
import { ContentType, HttpMethod, isRequestMiddleware } from "./types";
import type {
	Data,
	Middleware,
	MiddlewareConsumer,
	Params,
	RequestMiddleware,
	SearchParams,
} from "./types";

export class RequestBuilder implements MiddlewareConsumer {
	private _url: string;
	private _config: RequestInit;
	private _headers: Headers;
	private _middlewares: Set<Middleware>;

	constructor(url: string, method: HttpMethod = HttpMethod.Get) {
		this._url = url;
		this._headers = new Headers();
		this._config = {
			method,
		};
		this._middlewares = new Set();
	}

	public searchParams(params: SearchParams): this {
		const url = new URL(this._url);
		const searchParams = new URLSearchParams(url.search);

		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined && value !== null) {
				if (typeof value !== "object") {
					searchParams.set(key, value.toString());
				}
				// NOTE: (bwilytsch): Check what to do when object
			}
		}

		url.search = searchParams.toString();
		this._url = url.toString();

		return this;
	}

	// NOTE: (bwilytsch): should these parameters be case insensitive?
	public params(params: Params): this {
		const allParamKeys = Object.keys(params);
		const [newUrl, dynamicParamKeys] = synthesiseUrl(this._url, params);

		const url = new URL(newUrl);

		if (dynamicParamKeys.length < allParamKeys.length) {
			for (const key of allParamKeys) {
				if (!dynamicParamKeys.includes(key.toLowerCase())) {
					const value = params[key];
					if (value === undefined) {
						throw new Error(`Missing value for parameter: ${key}`);
					}

					if (
						typeof value === "string" ||
						typeof value === "number" ||
						typeof value === "boolean"
					) {
						url.searchParams.append(key, value.toString());
					}
				}
			}
		}

		this._url = url.toString();

		return this;
	}

	public data(data: Data): this {
		if (data instanceof File) {
			// NOTE: (bwilytsch): Not sure what `Content-Type` should be here
			this._headers.set("Content-Type", ContentType.MultiFile);
			this._config.body = data;
		}

		if (data instanceof FormData) {
			// FormData sets its own appropriate Content-Type with boundary
			// Don't set Content-Type header manually as it will cause issues
			this._config.body = data;
			return this;
		}

		if (typeof data === "object") {
			this._headers.set("Content-Type", ContentType.Json);
			this._config.body = JSON.stringify(data);
		}

		return this;
	}

	public use(middleware: Middleware | Set<Middleware>): this {
		if (middleware instanceof Set) {
			for (const m of Array.from(middleware)) {
				this._middlewares.add(m);
			}
		} else {
			this._middlewares.add(middleware);
		}

		return this;
	}

	public async build(): Promise<Request> {
		const request = new Request(this._url, {
			...this._config,
			headers: this._headers,
		});

		const requestMiddleware: RequestMiddleware[] = Array.from(
			this._middlewares,
		).filter(isRequestMiddleware);

		await Promise.all(requestMiddleware.map((m) => m.onRequest(request)));

		return request;
	}
}
